'use strict';

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

var _isEmpty = require('lodash/isEmpty');
var _isNil = require('lodash/isNil');
var _last = require('lodash/last');
var _get = require('lodash/get');

var validateRulesForValue = require('./validate-rules-for-value');
var validators = require('./validators');
var ValueValidationError = require('./models/value-validation-error');

module.exports = validateValue;

/**
 * Validates a value (recursively) against a given validation options object and returns an object
 * containing failure messages of any value properties.
 * @param {object} indexedFields An object, keyed by objectKey, containing the fields for the entity type
 * @param {object} fieldKey The original field key to include in the validation
 * @param {object} field The field associated with the value to validate
 * @param {any} value The value to validate
 * @param {boolean} checkDefined By default, null/undefined values will not fail validation.
 * If set to true, they will.
 * @param {string} failureKey The item specific key to be used for any error messages on the value
 * @param {Array} errorsArray The errors array accumulator (to be used in recursive calls)
 * @return {Array} An array containing any failed validation items, with the format:
 * {
 *   field: {fieldKey},
 *   path: {failureMessagesKey},
 *   messages: ["error", "messages"]
 * }
 */
function validateValue(indexedFields, fieldKey, field, value, checkDefined) {
  var failureKey = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : fieldKey;
  var errorsArray = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : [];

  var resultingErrors = [].concat(_toConsumableArray(errorsArray));

  var validationOptions = _get(field, 'input.options') ? _extends({}, field.validation, {
    _selectOptions: _get(field, 'input.options'),
    postChildren: _get(field, 'validation.postChildren') ? _extends({}, field.validation.postChildren, { _selectOptions: _get(field, 'input.options') }) : undefined
  }) : field.validation;

  if (!validationOptions) return resultingErrors;

  var rules = validationOptions.rules || [];

  if (rules.includes('required')) {
    //Check required before null/undefined ignore. If this field is required, and a nil value was passed,
    //we still want the item to fail validation even though we're ignoring empty keys by default
    var requiredValidationResult = validators.required(value, validationOptions);

    if (!requiredValidationResult.isValid) {
      var validationError = new ValueValidationError(fieldKey, failureKey, requiredValidationResult.failureMessages);

      return [].concat(_toConsumableArray(resultingErrors), [validationError]);
    }
  }

  if (!checkDefined && _isNil(value)) return resultingErrors;

  resultingErrors = [].concat(_toConsumableArray(resultingErrors), _toConsumableArray(validateRulesForValue(validationOptions, value, fieldKey, failureKey)));

  //Skip child validation if collection validation fails
  if (_isEmpty(resultingErrors)) {
    //If the field is a collection type
    if (field.item) {
      var itemField = void 0;

      if (field.item.type && field.item.type.startsWith('@fields')) {
        var itemFieldKey = field.item.type.slice(field.item.type.indexOf('.') + 1);

        itemField = indexedFields[itemFieldKey];

        if (!itemField) {
          throw new Error('Field with key ' + itemFieldKey + ' does not exist');
        }
      } else {
        itemField = { validation: field.item.validation };
      }

      value.forEach(function (item, index) {
        var itemKey = failureKey + '[' + index + ']';

        resultingErrors = validateValue(indexedFields, fieldKey, itemField, item, true, itemKey, resultingErrors);
      });
    } else if (validationOptions.item) {
      //If there is legacy item validation
      value.forEach(function (item, index) {
        var itemKey = failureKey + '[' + index + ']';

        var itemField = { validation: validationOptions.item };

        resultingErrors = validateValue(indexedFields, fieldKey, itemField, item, true, itemKey, resultingErrors);
      });
    }

    //If the field is an object type
    if (field.fields) {
      var valueKeys = Object.keys(value);

      if (field.autocomplete) {
        //If this is an autocompleteable object field, we want to alllow the edit payload to include an 
        //autocomplete item instead of the eventual value
        var acItemValidationResult = validators.isAutocompleteItem(value);

        if (acItemValidationResult.isValid) {
          return resultingErrors;
        }
      }

      valueKeys.forEach(function (key) {
        var matchingFieldKey = field.fields.find(function (fieldsKey) {
          return _last(fieldsKey.split('.')) === key;
        });
        var matchingField = indexedFields[matchingFieldKey];

        if (matchingField) {
          var itemKey = failureKey + '.' + key;

          resultingErrors = validateValue(indexedFields, fieldKey, matchingField, value[key], false, itemKey, resultingErrors);
        } else {
          delete value[key];
        }
      });

      field.fields.forEach(function (fieldsKey) {
        var matchingField = indexedFields[fieldsKey];

        var fieldValidation = matchingField.validation || { rules: [] };

        var key = _last(fieldsKey.split('.'));

        if (fieldValidation.rules.includes('required') && !valueKeys.includes(key)) {
          var validationResult = validators.required(value[key], fieldValidation);

          resultingErrors = [].concat(_toConsumableArray(resultingErrors), [new ValueValidationError(fieldKey, failureKey + '.' + key, validationResult.failureMessages)]);
        }
      });
    } else if (validationOptions.keys) {
      //If there is legacy object key validation
      var _valueKeys = Object.keys(value);
      var validationKeys = Object.keys(validationOptions.keys);

      _valueKeys.forEach(function (key) {
        if (validationOptions.keys[key]) {
          var itemKey = failureKey + '.' + key;
          resultingErrors = validateValue(indexedFields, fieldKey, { validation: validationOptions.keys[key] }, value[key], false, itemKey, resultingErrors);
        } else {
          //TODO: Move to somewhere else? Parent level keys are filtered out elsewhere, so we could move to there.
          //However, we are basing whether or not to remove this on validation options.
          delete value[key];
        }
      });

      validationKeys.forEach(function (key) {
        var keyValidation = validationOptions.keys[key];

        if (keyValidation.rules && keyValidation.rules.includes('required') && !_valueKeys.includes(key)) {
          var validationResult = validators.required(value[key], keyValidation);

          resultingErrors = [].concat(_toConsumableArray(resultingErrors), [new ValueValidationError(fieldKey, failureKey + '.' + key, validationResult.failureMessages)]);
        }
      });
    }

    if (validationOptions.postChildren) {
      //This validation will be run after child validation succeeds. 
      //Should be used for parent rules that are dependent on children being valid
      resultingErrors = [].concat(_toConsumableArray(resultingErrors), _toConsumableArray(validateRulesForValue(validationOptions.postChildren, value, fieldKey, failureKey)));
    }
  }

  return resultingErrors;
}